import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import { EASE } from "./motionPrimitives";
import { scrollToHash } from "./Navbar";

const LINES = [
    <>
        Marketing, SEO & <span className="text-gold">AI</span>
    </>,
    <>engineered for</>,
    <>
        relentless <span className="text-gold">growth.</span>
    </>,
];

const STATS = [
    { value: "60+", label: "growth partners launched" },
    { value: "98%", label: "client retention rate" },
    { value: "24h", label: "average response time" },
];

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const yBg = useTransform(scrollYProgress, [0, 1], [0, 140]);
    const yMark = useTransform(scrollYProgress, [0, 1], [0, 240]);
    const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

    return (
        <section
            ref={ref}
            className="relative min-h-screen bg-navy overflow-hidden flex items-center"
        >
            <motion.div style={{ y: yBg }} className="absolute inset-0 pointer-events-none">
                <div
                    className="absolute inset-0 opacity-60"
                    style={{
                        backgroundImage:
                            "linear-gradient(rgba(255,255,255,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.035) 1px, transparent 1px)",
                        backgroundSize: "88px 88px",
                    }}
                />
                <div className="absolute -top-40 right-[-10%] w-[46rem] h-[46rem] rounded-full bg-gold/15 blur-[140px]" />
                <div className="absolute bottom-[-30%] left-[-10%] w-[38rem] h-[38rem] rounded-full bg-navy-light/70 blur-[120px]" />
            </motion.div>

            <motion.div
                style={{ y: yMark }}
                aria-hidden="true"
                className="absolute bottom-[-4vw] right-[-2vw] select-none pointer-events-none"
            >
                <span className="font-display font-extrabold text-[20vw] leading-none text-outline tracking-tight">
                    SMART NET
                </span>
            </motion.div>

            <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-36 pb-24">
                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: EASE }}
                    className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.35em] text-gold font-semibold"
                >
                    SMART NET INC — Fort Lauderdale, Florida
                </motion.p>

                <h1
                    data-testid="hero-headline"
                    className="mt-6 font-display font-extrabold tracking-tight leading-[1.04] text-4xl sm:text-6xl lg:text-7xl text-white"
                >
                    {LINES.map((line, i) => (
                        <span key={i} className="block overflow-hidden pb-1">
                            <motion.span
                                className="block"
                                initial={{ y: "112%" }}
                                animate={{ y: 0 }}
                                transition={{
                                    duration: 0.9,
                                    delay: 0.15 + i * 0.13,
                                    ease: EASE,
                                }}
                            >
                                {line}
                            </motion.span>
                        </span>
                    ))}
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.55, ease: EASE }}
                    className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-mist"
                >
                    SMART NET INC builds revenue engines for ambitious companies —
                    performance marketing, technical SEO, custom AI systems and
                    lead generation, all under one roof.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.7, ease: EASE }}
                    className="mt-10 flex flex-col sm:flex-row gap-4"
                >
                    <button
                        data-testid="hero-primary-cta"
                        onClick={() => scrollToHash("#contact")}
                        className="btn-shine inline-flex items-center justify-center gap-2 bg-gold text-navy font-bold px-8 py-4 rounded-full hover:bg-gold-hover transition-colors"
                    >
                        Book a strategy call
                        <ArrowUpRight size={18} />
                    </button>
                    <button
                        data-testid="hero-secondary-cta"
                        onClick={() => scrollToHash("#results")}
                        className="inline-flex items-center justify-center gap-2 border border-white/25 text-white font-semibold px-8 py-4 rounded-full hover:border-gold hover:text-gold transition-colors"
                    >
                        See client results
                    </button>
                </motion.div>

                <motion.div
                    data-testid="hero-stat-badge"
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
                    className="mt-16 grid grid-cols-3 max-w-2xl divide-x divide-white/10 border-y border-white/10 py-6"
                >
                    {STATS.map((s) => (
                        <div key={s.label} className="px-4 first:pl-0">
                            <p className="font-mono text-2xl sm:text-3xl font-semibold text-gold">
                                {s.value}
                            </p>
                            <p className="mt-1 text-[11px] sm:text-xs uppercase tracking-wider text-mist">
                                {s.label}
                            </p>
                        </div>
                    ))}
                </motion.div>
            </div>

            <motion.button
                style={{ opacity: fade }}
                data-testid="hero-scroll-cue"
                onClick={() => scrollToHash("#services")}
                className="absolute bottom-6 left-1/2 -translate-x-1/2 text-mist hover:text-gold transition-colors"
                aria-label="Scroll to services"
            >
                <motion.span
                    animate={{ y: [0, 8, 0] }}
                    transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
                    className="block"
                >
                    <ChevronDown size={26} />
                </motion.span>
            </motion.button>
        </section>
    );
}
